import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
    getHiddenAddressesWithDeliveries,
    purgeDeliveriesForHiddenAddress,
    purgeDeliveriesForHiddenAddresses
} from "../api/addresses";
import type { HiddenAddressWithDeliveries } from "../api/addresses/types";
import { showError, showSuccess } from "../shared/toast";
import "./hidden-addresses-panel.css";

const QUERY_KEY = ["addresses", "hidden-with-deliveries"];

interface PostalGroup {
    postalCode: string;
    city: string;
    addresses: HiddenAddressWithDeliveries[];
    deliveryCount: number;
}

function formatAddressLine(a: { street: string; number?: string | null; apartment?: string | null }): string {
    const parts = [a.street, a.number].filter(Boolean).join(" ");
    return a.apartment ? `${parts}, ${a.apartment}` : parts;
}

function formatDate(iso?: string | null): string {
    if (!iso) {
        return "–";
    }
    return new Date(iso).toLocaleDateString("sv-SE", { year: "numeric", month: "short", day: "numeric" });
}

function groupByPostalCode(addresses: HiddenAddressWithDeliveries[]): PostalGroup[] {
    const groups = new Map<string, PostalGroup>();
    for (const address of addresses) {
        const key = address.postalCode || "Okänt postnummer";
        let group = groups.get(key);
        if (!group) {
            group = { postalCode: key, city: address.city, addresses: [], deliveryCount: 0 };
            groups.set(key, group);
        }
        group.addresses.push(address);
        group.deliveryCount += address.deliveryCount;
    }
    return Array.from(groups.values()).sort((a, b) => a.postalCode.localeCompare(b.postalCode, "sv"));
}

export const HiddenAddressesPanel = (): JSX.Element => {
    const queryClient = useQueryClient();
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const { data: addresses = [], isLoading, error } = useQuery({
        queryKey: QUERY_KEY,
        queryFn: getHiddenAddressesWithDeliveries
    });

    const groups = useMemo(() => groupByPostalCode(addresses), [addresses]);

    const clearSelectionAndRefresh = async () => {
        setSelected(new Set());
        await queryClient.invalidateQueries({ queryKey: QUERY_KEY });
    };

    const purgeMutation = useMutation({
        mutationFn: purgeDeliveriesForHiddenAddress,
        onSuccess: async (data) => {
            showSuccess(`${data.deletedCount} utdelningar raderade`);
            await clearSelectionAndRefresh();
        },
        onError: (e) => {
            const msg = e instanceof Error ? e.message : "Okänt fel";
            showError("Kunde inte radera utdelningarna", msg);
        }
    });

    const bulkPurgeMutation = useMutation({
        mutationFn: purgeDeliveriesForHiddenAddresses,
        onSuccess: async (data) => {
            showSuccess(`${data.deletedCount} utdelningar raderade för ${data.purgedAddressCount} adresser`);
            await clearSelectionAndRefresh();
        },
        onError: (e) => {
            const msg = e instanceof Error ? e.message : "Okänt fel";
            showError("Kunde inte radera valda utdelningar", msg);
        }
    });

    const toggleAddress = (id: string) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) {
                next.delete(id);
            } else {
                next.add(id);
            }
            return next;
        });
    };

    const toggleGroup = (group: PostalGroup) => {
        const groupIds = group.addresses.map((a) => a.id);
        const allSelected = groupIds.every((id) => selected.has(id));
        setSelected((prev) => {
            const next = new Set(prev);
            groupIds.forEach((id) => (allSelected ? next.delete(id) : next.add(id)));
            return next;
        });
    };

    const toggleAll = () => {
        setSelected((prev) => (prev.size === addresses.length ? new Set() : new Set(addresses.map((a) => a.id))));
    };

    const selectedAddresses = useMemo(
        () => addresses.filter((a) => selected.has(a.id)),
        [addresses, selected]
    );
    const selectedDeliveryCount = selectedAddresses.reduce((sum, a) => sum + a.deliveryCount, 0);

    const handlePurge = (address: HiddenAddressWithDeliveries) => {
        const confirmed = window.confirm(
            `Radera ${address.deliveryCount} kvarvarande utdelningar för ${formatAddressLine(address)}, ${address.city}? Detta går inte att ångra.`
        );
        if (confirmed) {
            purgeMutation.mutate(address.id);
        }
    };

    const handleBulkPurge = () => {
        if (selected.size === 0) {
            return;
        }
        const confirmed = window.confirm(
            `Radera ${selectedDeliveryCount} kvarvarande utdelningar för ${selected.size} adresser? Detta går inte att ångra.`
        );
        if (confirmed) {
            bulkPurgeMutation.mutate(Array.from(selected));
        }
    };

    if (error) {
        return (
            <div className="hidden-addresses-panel">
                <h2>Dolda adresser</h2>
                <p className="error-message">Kunde inte ladda dolda adresser.</p>
            </div>
        );
    }

    return (
        <section className="hidden-addresses-panel" aria-label="Dolda adresser">
            <div className="hidden-addresses-panel__header">
                <h2>Dolda adresser</h2>
                <p className="hidden-addresses-panel__description">
                    Dolda adresser som fortfarande har kvarvarande utdelningar, grupperade på postnummer. Radera utdelningarna för att städa bort dem permanent.
                </p>
            </div>

            {isLoading ? (
                <p>Laddar...</p>
            ) : addresses.length === 0 ? (
                <p className="hidden-addresses-panel__empty">Inga dolda adresser med kvarvarande utdelningar.</p>
            ) : (
                <>
                    <div className="hidden-addresses-panel__toolbar">
                        <label className="hidden-addresses-panel__select-all">
                            <input
                                type="checkbox"
                                checked={selected.size > 0 && selected.size === addresses.length}
                                onChange={toggleAll}
                            />
                            Markera alla ({addresses.length})
                        </label>
                        {selected.size > 0 && (
                            <div className="hidden-addresses-panel__bulk-actions">
                                <span>
                                    {selected.size} adresser valda ({selectedDeliveryCount} utdelningar)
                                </span>
                                <button
                                    className="action-button action-button--delete"
                                    onClick={handleBulkPurge}
                                    disabled={bulkPurgeMutation.isPending}
                                >
                                    Radera valda utdelningar
                                </button>
                            </div>
                        )}
                    </div>

                    {groups.map((group) => {
                        const groupIds = group.addresses.map((a) => a.id);
                        const groupSelectedCount = groupIds.filter((id) => selected.has(id)).length;
                        return (
                            <div className="hidden-addresses-panel__group" key={group.postalCode}>
                                <div className="hidden-addresses-panel__group-header">
                                    <label className="hidden-addresses-panel__group-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={groupSelectedCount === groupIds.length}
                                            ref={(el) => {
                                                if (el) {
                                                    el.indeterminate = groupSelectedCount > 0 && groupSelectedCount < groupIds.length;
                                                }
                                            }}
                                            onChange={() => toggleGroup(group)}
                                        />
                                        <strong>{group.postalCode}</strong> {group.city}
                                    </label>
                                    <span className="hidden-addresses-panel__group-meta">
                                        {group.addresses.length} adresser · {group.deliveryCount} utdelningar
                                    </span>
                                </div>
                                <div className="hidden-addresses-panel__list">
                                    <table className="hidden-addresses-table">
                                        <thead>
                                            <tr>
                                                <th></th>
                                                <th>Adress</th>
                                                <th>Antal utdelningar</th>
                                                <th>Senaste utdelning</th>
                                                <th>Åtgärd</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {group.addresses.map((address) => (
                                                <tr key={address.id}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={selected.has(address.id)}
                                                            onChange={() => toggleAddress(address.id)}
                                                            aria-label={`Markera ${formatAddressLine(address)}`}
                                                        />
                                                    </td>
                                                    <td>{formatAddressLine(address)}</td>
                                                    <td>{address.deliveryCount}</td>
                                                    <td>{formatDate(address.lastDeliveredAt)}</td>
                                                    <td>
                                                        <button
                                                            className="action-button action-button--delete"
                                                            onClick={() => handlePurge(address)}
                                                            disabled={purgeMutation.isPending || bulkPurgeMutation.isPending}
                                                        >
                                                            Radera utdelningar
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        );
                    })}
                </>
            )}
        </section>
    );
};
